import { getStringSameStartChars } from "app/tools/common"
import { CampaignAPI, LookupAPI } from "generated-api"
import { Polygon } from "geojson"
import { geometrySVC } from "geometry/service"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import {
  ICampaignCityShapeDTO,
  IGeoShape,
  IGeoShapeCity,
  IGeoShapeCityData,
  IGeoShapeCountry,
  IGeoShapeCountryDTO,
  IGeoShapeCountryData,
  IGeoShapePostalCode,
  IGeoShapePostalCodeData,
  IGeoShapeState,
  IGeoShapeStateData,
  IGeoShapeStateShapeDTO,
  ILookupCity,
  IShapeRepository,
  SHAPE_TYPES
} from "./model"

export const shapeRPO: IShapeRepository = {
  // CITY
  searchCityByName(cityName: string): Promise<ILookupCity[]> {
    return new Promise<ILookupCity[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiCityShapeList(0, undefined, cityName)
          .then(response =>
            resolve(
              response.map(r => ({ city_shape_id: r.id, country: r.country_id, name: r.name, state: r.state_id }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while searching city by name", error)
            reject("shape.get.city.by.name.error.server")
          })
      } catch (ex) {
        console.error("Server error while searching city by name", ex)
        reject("shape.get.city.by.name.error.unexpected")
      }
    })
  },
  getCitiesByNames(names: string[]): Promise<ILookupCity[]> {
    return new Promise<ILookupCity[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiCityShapeList(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          names
        )
          .then(response =>
            resolve(
              response.map(r => ({ city_shape_id: r.id, country: r.country_id, name: r.name, state: r.state_id }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while getting city by ids", error)
            reject("shape.get.city.by.ids.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting city by ids", ex)
        reject("shape.get.city.by.ids.error.unexpected")
      }
    })
  },
  getCityShapeData(cityId: number): Promise<IGeoShapeCityData> {
    return new Promise<IGeoShapeCityData>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiCityShape(cityId)
          .then(response => {
            resolve({
              city_shape_id: response.id,
              country_id: response.country_id,
              state_id: response.state_id,
              external_id: response.external_id,
              classification: response.classification,
              name: response.name,
              area: response.area,
              longitude: response.longitude,
              latitude: response.latitude,
              shape: response.shape as Polygon
            })
          })
          .catch((error: any) => {
            console.error("Server error while getting city data", error)
            reject("shape.get.city.data.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting city data", ex)
        reject("shape.get.city.data.error.unexpected")
      }
    })
  },
  searchCities(filter: string): Promise<IGeoShapeCity[]> {
    return new Promise<IGeoShapeCity[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiCityShapeList(undefined, undefined, filter, "starts_with")
          .then(response =>
            resolve(
              response.map(c => ({
                ...c,
                type: SHAPE_TYPES.CITY,
                city_shape_id: c.id,
                shape: undefined as any
              }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while searching cities", error)
            reject("shape.search.cities.error.server")
          })
      } catch (ex) {
        console.error("Server error while searching cities", ex)
        reject("shape.search.cities.error.unexpected")
      }
    })
  },
  getAssociatedCities(campaignId: number): Promise<IGeoShapeCity[]> {
    return new Promise<IGeoShapeCity[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignCityShapeList(campaignId)
          .then(async response => resolve(await getAllCitiesShapes(response)))
          .catch((error: any) => {
            console.error("Server error while getting campaign's cities", error)
            reject("shape.get.cities.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting campaign's cities", ex)
        reject("shape.get.cities.error.unexpected")
      }
    })
  },
  setAssociatedCities(campaignId: number, cities: IGeoShapeCity[]): Promise<IGeoShapeCity[]> {
    return new Promise<IGeoShapeCity[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        const cityById: { [cityShapeId: number]: IGeoShapeCity } = {}
        for (const city of cities) {
          cityById[city.city_shape_id] = city
        }
        CampaignAPI.postApiCampaignCityShapeList(
          campaignId,
          cities.map(c => ({
            campaign_id: campaignId,
            city_shape_id: c.city_shape_id,
            is_active: true
          }))
        )
          .then(response => {
            const newCities: IGeoShapeCity[] = response.map(newCity => {
              const mappedCity = cityById[newCity.city_shape_id]
              if (!mappedCity) {
                console.error(`Mismatch in response for city="${newCity.city_shape_id}"`)
                throw Error("shape.set.cities.error.mapping")
              }
              return {
                ...mappedCity,
                ...newCity,
                shape: mappedCity.shape,
                type: SHAPE_TYPES.CITY
              }
            })
            resolve(newCities)
          })
          .catch((error: any) => {
            console.error("Server error while setting campaign's cities", error)
            reject("shape.set.cities.error.server")
          })
      } catch (ex) {
        console.error("Server error while setting campaign's cities", ex)
        reject("shape.set.cities.error.unexpected")
      }
    })
  },
  associateCityToCampaign(campaignId: number, city: IGeoShapeCity): Promise<IGeoShapeCity> {
    return new Promise<IGeoShapeCity>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignCityShape({
          campaign_id: campaignId,
          city_shape_id: city.city_shape_id,
          is_active: true
        })
          .then(response => {
            resolve({
              ...city,
              ...response,
              shape: city.shape,
              type: SHAPE_TYPES.CITY
            })
          })
          .catch((error: any) => {
            console.error("Server error while adding campaign's city", error)
            reject("shape.add.city.error.server")
          })
      } catch (ex) {
        console.error("Server error while adding campaign's city", ex)
        reject("shape.add.city.error.unexpected")
      }
    })
  },
  deassociateCityToCampaign(campaignId: number, cityAssociationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignCityShape(campaignId, cityAssociationId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting campaign's city", error)
            reject("shape.delete.city.error.server")
          })
      } catch (ex) {
        console.error("Server error while deleting campaign's city", ex)
        reject("shape.delete.city.error.unexpected")
      }
    })
  },
  // POSTAL CODE
  getPostalCodeData(postalCodeIds: number[], fallbackShape: IGeoShape): Promise<IGeoShapePostalCodeData> {
    return new Promise<IGeoShapePostalCodeData>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        if (postalCodeIds.length === 0) {
          return resolve(fallbackShape as any)
        } else if (postalCodeIds.length === 1) {
          LookupAPI.getApiPostalCodeShape(postalCodeIds[0])
            .then(response => {
              resolve({
                postal_code_shape_ids: [response.id],
                country_id: response.country_id,
                postal_code: response.postal_code,
                state_id: response.state_id,
                shape: response.shape as Polygon
              })
            })
            .catch((error: any) => {
              console.error("Server error while getting postal code data", error)
              reject("shape.get.postal-code.data.error.server")
            })
        } else {
          let postal_code: string = ""
          let country_id: string = ""
          let state_id: string = ""
          const postal_code_shape_ids: number[] = []
          const shapes: Polygon[] = []
          Promise.all(
            postalCodeIds.map(async postalCodeId =>
              LookupAPI.getApiPostalCodeShape(postalCodeId).then(response => {
                const postalCodeData = response
                if (!country_id) {
                  country_id = postalCodeData.country_id
                }
                if (!state_id) {
                  state_id = postalCodeData.state_id
                }
                if (!postal_code) {
                  postal_code = postalCodeData.postal_code
                } else {
                  postal_code = getStringSameStartChars(postal_code, postalCodeData.postal_code)
                }
                postal_code_shape_ids.push(postalCodeData.id)
                shapes.push(postalCodeData.shape as Polygon)
              })
            )
          )
            .then(() => {
              resolve({
                country_id,
                postal_code,
                postal_code_shape_ids,
                shape: geometrySVC.getMultiPolygonFeature(shapes, {}).geometry as any,
                state_id
              })
            })
            .catch(error => {
              console.error("Server error while getting postal code data", error)
              reject("shape.get.postal-code.data.error.server")
            })
        }
      } catch (ex) {
        console.error("Server error while getting postal code data", ex)
        reject("shape.get.postal-code.data.error.unexpected")
      }
    })
  },
  searchPostalCodes(filter: string): Promise<IGeoShapePostalCode[]> {
    return new Promise<IGeoShapePostalCode[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiPostalCodeShapeList(undefined, undefined, filter, "contains")
          .then(response =>
            resolve(
              response.map(p => ({
                id: p.id,
                postal_code_shape_ids: [p.id],
                country_id: p.country_id,
                state_id: p.state_id,
                postal_code: p.postal_code,
                shape: (p as any).shape,
                type: SHAPE_TYPES.POSTAL_CODE
              }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while searching postal code", error)
            reject("shape.search.postal-code.error.server")
          })
      } catch (ex) {
        console.error("Server error while searching postal code", ex)
        reject("shape.search.postal-code.error.unexpected")
      }
    })
  },
  getPostalCodes(postalCodes: string[]): Promise<IGeoShapePostalCode[]> {
    return new Promise<IGeoShapePostalCode[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiPostalCodeShapeList(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          postalCodes
        )
          .then(response =>
            resolve(
              response.map(p => ({
                id: p.id,
                postal_code_shape_ids: [p.id],
                country_id: p.country_id,
                state_id: p.state_id,
                postal_code: p.postal_code,
                shape: (p as any).shape,
                type: SHAPE_TYPES.POSTAL_CODE
              }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while searching postal code", error)
            reject("shape.search.postal-code.error.server")
          })
      } catch (ex) {
        console.error("Server error while searching postal code", ex)
        reject("shape.search.postal-code.error.unexpected")
      }
    })
  },
  getAssociatedPostalCodes(campaignId: number): Promise<IGeoShapePostalCode[]> {
    return new Promise<IGeoShapePostalCode[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignPostalCodeList(campaignId)
          .then(async rawPostalCodes => {
            const postalCodes: IGeoShapePostalCode[] = []
            // a postal code could be "332", so we need to fetch all PC that starts by "332"
            // in order to get the missing shapeIds
            Promise.all(
              rawPostalCodes.map(async rawPostalCode => {
                const foundPostalCodes = await this.searchPostalCodes(rawPostalCode.postal_code as string)
                postalCodes.push({
                  ...rawPostalCode,
                  postal_code: rawPostalCode.postal_code as string,
                  postal_code_shape_ids: foundPostalCodes.map(pc => pc.postal_code_shape_ids).flat(),
                  shape: (rawPostalCode as any).shape,
                  country_id: (rawPostalCode as any).country_id,
                  state_id: (rawPostalCode as any).state_id,
                  type: SHAPE_TYPES.POSTAL_CODE
                })
              })
            )
              .then(async () => resolve(await getAllPostalCodeShapes(postalCodes)))
              .catch(error => {
                console.error("Server error while getting all postal code shape ids", error)
                reject("shape.get.postal-code.error.server.shape.id")
              })
          })
          .catch((error: any) => {
            console.error("Server error while getting campaign's postal code", error)
            reject("shape.get.postal-code.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting campaign's postal code", ex)
        reject("shape.get.postal-code.error.unexpected")
      }
    })
  },
  associatePostalCodeToCampaign(campaignId: number, postalCode: IGeoShapePostalCode): Promise<IGeoShapePostalCode> {
    return new Promise<IGeoShapePostalCode>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignPostalCode({ campaign_id: campaignId, postal_code: postalCode.postal_code })
          .then(response => {
            response.shape = postalCode.shape
            response.postal_code = postalCode.postal_code
            response.type = SHAPE_TYPES.POSTAL_CODE
            resolve(response)
          })
          .catch((error: any) => {
            console.error("Server error while adding campaign's postal code", error)
            reject("shape.add.postal-code.error.server")
          })
      } catch (ex) {
        console.error("Server error while adding campaign's postal code", ex)
        reject("shape.add.postal-code.error.unexpected")
      }
    })
  },
  deassociatePostalCodeToCampaign(campaignId: number, postalCodeAssociationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignPostalCode(campaignId, postalCodeAssociationId)
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while deleteing campaign's postal code", error)
            reject("shape.delete.postal-code.error.server")
          })
      } catch (ex) {
        console.error("Server error while deleting campaign's postal code", ex)
        reject("shape.delete.postal-code.error.unexpected")
      }
    })
  },
  setPostalCodesToCampaign(campaignId: number, postalCodes: string[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignPostalCodeList(
          campaignId,
          postalCodes.map(p => ({ campaign_id: campaignId, postal_code: p }))
        )
          // the endpoint returns nothing ...
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while setting campaign's postal codes", error)
            reject("shape.set.postal-code.error.server")
          })
      } catch (ex) {
        console.error("Server error while setting campaign's postal codes", ex)
        reject("shape.set.postal-code.error.unexpected")
      }
    })
  },
  // STATES
  getStateData(stateId: string): Promise<IGeoShapeStateData> {
    return new Promise<IGeoShapeStateData>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiStateShape(stateId)
          .then(response => {
            resolve({
              country_id: response.country_id,
              state_shape_id: response.id,
              name: response.name || "",
              shape: response.shape as Polygon
            })
          })
          .catch((error: any) => {
            console.error("Server error while getting state data", error)
            reject("shape.get.state.data.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting state data", ex)
        reject("shape.get.state.data.error.unexpected")
      }
    })
  },
  searchStates(filter: string): Promise<IGeoShapeState[]> {
    return new Promise<IGeoShapeState[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiStateList(undefined, undefined, filter, "starts_with")
          .then(response => {
            resolve(
              response.map(s => ({
                state_shape_id: s.id,
                country_id: s.country_id,
                name: s.name || "",
                shape: (s as any).shape,
                id: -1,
                type: SHAPE_TYPES.STATE
              }))
            )
          })
          .catch((error: any) => {
            console.error("Server error while searching states", error)
            reject("shape.search.states.error.server")
          })
      } catch (ex) {
        console.error("Server error while searching states", ex)
        reject("shape.search.states.error.unexpected")
      }
    })
  },
  getStatesById(ids: string[]): Promise<IGeoShapeState[]> {
    return new Promise<IGeoShapeState[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiStateList(undefined, undefined, undefined, undefined, undefined, undefined, ids)
          .then(response => {
            resolve(
              response.map(s => ({
                state_shape_id: s.id,
                country_id: s.country_id,
                name: s.name || "",
                shape: (s as any).shape,
                id: -1,
                type: SHAPE_TYPES.STATE
              }))
            )
          })
          .catch((error: any) => {
            console.error("Server error while getting states by id", error)
            reject("shape.get.states.by.id.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting states by id", ex)
        reject("shape.get.states.by.id.error.unexpected")
      }
    })
  },
  getAssociatedStates(campaignId: number): Promise<IGeoShapeState[]> {
    return new Promise<IGeoShapeState[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignStateShapeList(campaignId)
          .then(async response => resolve(await getAllStateShapes(response)))
          .catch((error: any) => {
            console.error("Server error while getting campaign's states", error)
            reject("shape.get.states.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting campaign's states", ex)
        reject("shape.get.states.error.unexpected")
      }
    })
  },
  associateStateToCampaign(campaignId: number, state: IGeoShapeState): Promise<IGeoShapeState> {
    return new Promise<IGeoShapeState>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignStateShape({
          campaign_id: campaignId,
          state_id: state.state_shape_id,
          country_id: state.country_id
        })
          .then(response => {
            resolve({
              state_shape_id: response.state_id,
              country_id: response.country_id,
              name: state.name,
              shape: state.shape,
              id: state.id,
              type: SHAPE_TYPES.STATE
            })
          })
          .catch((error: any) => {
            console.error("Server error while adding campaign's state", error)
            reject("shape.add.state.error.server")
          })
      } catch (ex) {
        console.error("Server error while adding campaign's state", ex)
        reject("shape.add.state.error.unexpected")
      }
    })
  },
  deassociateStateToCampaign(campaignId: number, stateAssociationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignStateShape(campaignId, stateAssociationId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting campaign's state", error)
            reject("shape.delete.state.error.server")
          })
      } catch (ex) {
        console.error("Server error while deleting campaign's state", ex)
        reject("shape.delete.state.error.unexpected")
      }
    })
  },
  // COUNTRIES
  getCountryData(countryId: string): Promise<IGeoShapeCountryData> {
    return new Promise<IGeoShapeCountryData>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiCountryShape(countryId)
          .then(response => {
            resolve({
              alpha3: response.alpha3 as string,
              latitude: response.latitude,
              longitude: response.longitude,
              country_shape_id: response.id,
              name: response.name,
              shape: response.shape as Polygon
            })
          })
          .catch((error: any) => {
            console.error("Server error while getting country data", error)
            reject("shape.get.country.data.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting country data", ex)
        reject("shape.get.country.data.error.unexpected")
      }
    })
  },
  searchCountries(filter: string): Promise<IGeoShapeCountry[]> {
    return new Promise<IGeoShapeCountry[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiCountryList(undefined, undefined, filter, "starts_with")
          .then(response => {
            resolve(
              response.map(c => {
                const country: IGeoShapeCountry = {
                  id: -1,
                  shape: (c as any).shape,
                  type: SHAPE_TYPES.COUNTRY,
                  country_shape_id: c.id,
                  name: c.name,
                  alpha3: c.alpha3 || "",
                  longitude: c.longitude,
                  latitude: c.latitude
                }
                delete (country as any).id
                return country
              })
            )
          })
          .catch((error: any) => {
            console.error("Server error while searching countries", error)
            reject("shape.search.countries.error.server")
          })
      } catch (ex) {
        console.error("Server error while searching countries", ex)
        reject("shape.search.countries.error.unexpected")
      }
    })
  },
  getCountriesById(countryIds: string[]): Promise<IGeoShapeCountry[]> {
    return new Promise<IGeoShapeCountry[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        LookupAPI.getApiCountryList(undefined, undefined, undefined, undefined, undefined, undefined, countryIds)
          .then(response => {
            resolve(
              response.map(c => {
                const country: IGeoShapeCountry = {
                  id: -1,
                  shape: (c as any).shape,
                  type: SHAPE_TYPES.COUNTRY,
                  country_shape_id: c.id,
                  name: c.name,
                  alpha3: c.alpha3 || "",
                  longitude: c.longitude,
                  latitude: c.latitude
                }
                delete (country as any).id
                return country
              })
            )
          })
          .catch((error: any) => {
            console.error("Server error while getting countries by ids", error)
            reject("shape.get.countries.by.id.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting countries by ids", ex)
        reject("shape.search.countries.by.id.error.unexpected")
      }
    })
  },
  getAssociatedCountries(campaignId: number, fetchAllData?: boolean): Promise<IGeoShapeCountry[]> {
    return new Promise<IGeoShapeCountry[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignCountryShapeList(campaignId)
          .then(async response => resolve(await getAllCountryShapes(response)))
          .catch((error: any) => {
            console.error("Server error while getting campaign's countries", error)
            reject("shape.get.countries.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting campaign's countries", ex)
        reject("shape.get.countries.error.unexpected")
      }
    })
  },
  associateCountryToCampaign(campaignId: number, country: IGeoShapeCountry): Promise<IGeoShapeCountry> {
    return new Promise<IGeoShapeCountry>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignCountryShape({
          campaign_id: campaignId,
          country_id: country.country_shape_id
        })
          .then(response => {
            resolve({
              id: response.id,
              shape: country.shape,
              type: SHAPE_TYPES.COUNTRY,
              country_shape_id: response.country_id,
              name: country.name,
              alpha3: country.alpha3,
              longitude: country.longitude,
              latitude: country.latitude
            })
          })
          .catch((error: any) => {
            console.error("Server error while adding campaign's country", error)
            reject("shape.add.country.error.server")
          })
      } catch (ex) {
        console.error("Server error while adding campaign's country", ex)
        reject("shape.add.country.error.unexpected")
      }
    })
  },
  deassociateCountryToCampaign(campaignId: number, countryAssociationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignCountryShape(campaignId, countryAssociationId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting campaign's country", error)
            reject("shape.delete.country.error.server")
          })
      } catch (ex) {
        console.error("Server error while deleting campaign's country", ex)
        reject("shape.delete.country.error.unexpected")
      }
    })
  }
}

async function getAllCitiesShapes(cityDTOs: ICampaignCityShapeDTO[]): Promise<IGeoShapeCity[]> {
  const filledCities: IGeoShapeCity[] = []
  await Promise.all(
    cityDTOs.map(async cityDTO =>
      filledCities.push({
        ...cityDTO,
        type: SHAPE_TYPES.CITY,
        ...(await shapeRPO.getCityShapeData(cityDTO.city_shape_id))
      })
    )
  )
  return filledCities
}

async function getAllPostalCodeShapes(postalCodes: IGeoShapePostalCode[]): Promise<IGeoShapePostalCode[]> {
  const filledPostalCodes: IGeoShapePostalCode[] = []
  await Promise.all(
    postalCodes.map(async postalCode =>
      filledPostalCodes.push({
        ...postalCode,
        type: SHAPE_TYPES.POSTAL_CODE,
        ...(await shapeRPO.getPostalCodeData(postalCode.postal_code_shape_ids, postalCode))
      })
    )
  )
  return filledPostalCodes
}

async function getAllStateShapes(states: IGeoShapeStateShapeDTO[]): Promise<IGeoShapeState[]> {
  const filledStates: IGeoShapeState[] = []
  await Promise.all(
    states.map(async state =>
      filledStates.push({
        ...state,
        type: SHAPE_TYPES.STATE,
        ...(await shapeRPO.getStateData((state as any).state_shape_id || state.state_id))
      })
    )
  )
  return filledStates
}

async function getAllCountryShapes(countryDTOs: IGeoShapeCountryDTO[]): Promise<IGeoShapeCountry[]> {
  const filledCountries: IGeoShapeCountry[] = []
  await Promise.all(
    countryDTOs.map(async countryDTO =>
      filledCountries.push({
        ...countryDTO,
        type: SHAPE_TYPES.COUNTRY,
        ...(await shapeRPO.getCountryData((countryDTO as any).country_shape_id || countryDTO.country_id))
      })
    )
  )
  return filledCountries
}
